import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Tech Distribution Australia';
const BASE_URL = 'https://techdistribution.com.au';
const DEFAULT_IMAGE = 'https://techdistribution.com.au/og-image.png';

export default function SEO({ 
  title, 
  description, 
  keywords,
  image,
  url,
  type = 'website',
  product = null,
  noindex = false
}) {
  const pageTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const pageDescription = description || 'Australia\'s trusted distributor for networking equipment, solar controllers, power systems, and outdoor enclosures. Trade pricing available.';
  const pageImage = image || DEFAULT_IMAGE;
  const pageUrl = url ? `${BASE_URL}${url}` : BASE_URL;
  const pageKeywords = keywords || 'tech distribution, networking equipment, solar controllers, industrial technology, Australia, trade pricing';

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <link rel="canonical" href={pageUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph Tags */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_AU" />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />

      {/* Product-specific structured data */}
      {product && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": product.name,
            "description": product.description,
            "image": product.images?.[0] || DEFAULT_IMAGE,
            "sku": product.sku,
            "brand": {
              "@type": "Brand",
              "name": product.brand
            },
            "offers": {
              "@type": "Offer",
              "url": pageUrl,
              "priceCurrency": "AUD",
              "price": product.retail_price,
              "availability": product.stock_quantity > 0 
                ? "https://schema.org/InStock" 
                : "https://schema.org/OutOfStock",
              "seller": {
                "@type": "Organization",
                "name": SITE_NAME
              }
            }
          })}
        </script>
      )}
    </Helmet>
  );
}

// Organization structured data for homepage
export function OrganizationSchema() {
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": SITE_NAME,
          "url": BASE_URL,
          "logo": `${BASE_URL}/logo.png`,
          "description": "Australia's trusted distributor for networking equipment, solar controllers, power systems, and outdoor enclosures.",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "AU"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+61-XXX-XXX-XXX",
            "contactType": "sales",
            "areaServed": "AU"
          },
          "sameAs": [
            "https://facebook.com/techdistribution",
            "https://instagram.com/techdistribution"
          ]
        })}
      </script>
    </Helmet>
  );
}

// Breadcrumb structured data
export function BreadcrumbSchema({ items }) {
  if (!items || items.length === 0) return null;
  
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": items.map((item, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": item.name,
            "item": `${BASE_URL}${item.url}`
          }))
        })}
      </script>
    </Helmet>
  );
}

// Local Business Schema
export function LocalBusinessSchema() {
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": SITE_NAME,
          "image": `${BASE_URL}/logo.png`,
          "priceRange": "$$",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "Australia"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": -33.8688,
            "longitude": 151.2093
          },
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "09:00",
            "closes": "17:00"
          }
        })}
      </script>
    </Helmet>
  );
}
